import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../utils/api'

/**
 * Presence heartbeat for the tickets page.
 *
 * Tells the server "I am on ticket X (and typing)" the instant that
 * changes, and again every PRESENCE_INTERVAL_MS while the tab is visible.
 * Each answer carries where everyone else is and an activity token that
 * changes whenever any ticket changes; the page refreshes its list only
 * when that token moves.
 *
 * Presence is best-effort: a failed heartbeat is logged and the next tick
 * tries again. Once the server has been unreachable for as long as its own
 * viewer TTL, the colleagues we last saw are dropped — we no longer know.
 */

export const PRESENCE_INTERVAL_MS = 5000
/** Mirrors PRESENCE_TTL_SECONDS on the worker. */
export const PRESENCE_STALE_MS = 15000
const PRESENCE_ENDPOINT = '/api/tickets/presence'
const EMPTY = { viewersByTicket: {}, activityToken: null }

export function useTicketPresence({ ticketId, composing, enabled = true }) {
  const [state, setState] = useState(EMPTY)
  const latestRequest = useRef(0)
  const lastSuccessAt = useRef(Date.now())

  const publish = useCallback(async () => {
    const requestNumber = ++latestRequest.current
    try {
      const response = await sendInOrder({ ticket_id: ticketId ?? null, composing: !!composing })
      if (requestNumber !== latestRequest.current) return
      lastSuccessAt.current = Date.now()
      setState({ viewersByTicket: response?.viewers || {}, activityToken: response?.activity_token ?? null })
    } catch (error) {
      console.warn('Ticket presence heartbeat failed:', error)
      if (Date.now() - lastSuccessAt.current >= PRESENCE_STALE_MS) {
        setState(previous => ({ ...previous, viewersByTicket: {} }))
      }
    }
  }, [ticketId, composing])

  useEffect(() => {
    if (!enabled) return undefined
    publish()
    const publishIfVisible = () => { if (document.visibilityState === 'visible') publish() }
    const tick = () => { if (!requestsPending()) publishIfVisible() }
    const interval = setInterval(tick, PRESENCE_INTERVAL_MS)
    document.addEventListener('visibilitychange', publishIfVisible)
    window.addEventListener('focus', publishIfVisible)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', publishIfVisible)
      window.removeEventListener('focus', publishIfVisible)
    }
  }, [publish, enabled])

  useEffect(() => {
    if (!enabled) return undefined
    return () => {
      sendInOrder({ ticket_id: null, composing: false }).catch(error => {
        console.warn('Ticket presence sign-off failed:', error)
      })
    }
  }, [enabled])

  return state
}

// Heartbeats leave one at a time, in the order they were asked for, so an
// unmount's sign-off can never be processed after the next mount's first
// heartbeat and wipe the fresh row. Shared across instances on purpose.
let queue = Promise.resolve()
let pending = 0

function sendInOrder(body) {
  pending += 1
  const request = queue.then(() => api.post(PRESENCE_ENDPOINT, body))
  queue = request.catch(() => {}).finally(() => { pending -= 1 })
  return request
}

function requestsPending() {
  return pending > 0
}
